import React, { useState } from 'react';
import { Search, Database, FileText, CreditCard, Clock, CheckCircle } from 'lucide-react';
import { useSearch } from '../hooks/useSearch';

const SmartSearchPanel = ({ onSearchResult }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [lastResult, setLastResult] = useState(null);
  
  const { loading, searchUniversalTerm } = useSearch();

  // Detectar tipo de busca automaticamente
  const detectSearchType = (term) => {
    if (!term) return 'unknown';
    
    const cleanTerm = term.replace(/\D/g, '');
    
    if (cleanTerm.length === 11) return 'cpf';
    if (cleanTerm.length === 14) return 'cnpj';
    if (term.toLowerCase().includes('pc') || cleanTerm.length < 11) return 'pc_code';
    
    return 'unknown';
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    
    let result = null;
    
    try {
      // Sempre usar busca universal para tudo
      result = await searchUniversalTerm(searchTerm);
      
      // ✅ LOG SIMPLES DA ORIGEM
      if (result?.searchSource) {
        const origem = result.searchSource === 'local_database' ? '🗄️ BANCO LOCAL' : 
                      result.searchSource === 'api_fallback' ? '🌐 API EXTERNA' :
                      result.searchSource === 'api_and_saved_to_local' ? '🌐➡️🗄️ API → BANCO' : 
                      '❓ ORIGEM DESCONHECIDA';
        console.log(`📊 ORIGEM: ${origem} (${result.executionTime || 0}ms)`);
      }
      
      setLastResult(result);
      
      // Enviar resultado para o painel do meio se a função foi passada
      console.log('🔍 SmartSearchPanel: onSearchResult disponível?', !!onSearchResult, 'result:', !!result);
      if (onSearchResult && result) {
        console.log('🔍 SmartSearchPanel: enviando resultado', { success: result.success });
        if (result.success) {
          // Processar os dados - pode vir como array de múltiplas cobranças
          let formattedData = [];
          
          if (Array.isArray(result.data) && result.data.length > 0) {
            // Se for array de resultados (múltiplas cobranças)
            formattedData = result.data.map(item => {
              // Cada item pode ter a estrutura { data: {...}, code: '...', ... } ou ser o próprio objeto
              return item.data || item;
            });
          } else if (result.data) {
            // Se for objeto único
            formattedData = [result.data];
          }
          
          console.log(`🔍 SmartSearchPanel: Processados ${formattedData.length} registro(s)`);
          
          // Criar um resultado no formato esperado pelo ResultsPanel
          // Passar TODOS os dados, não apenas o primeiro
          const formattedResult = {
            name: 'Busca Inteligente',
            color: 'blue',
            found: true,
            data: formattedData, // Array com TODAS as cobranças encontradas
            rawData: formattedData,
            status: 'found',
            foundParcela: result.parcela || formattedData[0]?.codigo_falante_radar || formattedData[0]?.codigoFalanteRadar,
            endpoint: result.endpoint,
            searchMethod: result.searchMethod,
            searchSource: result.searchSource,
            searchType: result.searchType,
            executionTime: result.executionTime,
            testedParcelas: result.testedParcelas || [],
            allResults: result.data || [],
            totalFound: result.totalFound || formattedData.length
          };
          
          console.log('🔍 SmartSearchPanel: chamando onSearchResult com formattedResult:', formattedResult);
          console.log(`🔍 SmartSearchPanel: Total de cobranças no resultado: ${formattedData.length}`);
          onSearchResult('smartSearch', formattedResult);
        } else {
          // Resultado não encontrado
          onSearchResult('smartSearch', {
            name: 'Busca Inteligente',
            color: 'blue',
            found: false,
            data: null,
            status: 'not_found',
            error: result.message || 'Não encontrado'
          });
        }
      }
    } catch (error) {
      console.error('Erro na busca:', error);
      
      // Enviar erro para o painel do meio
      if (onSearchResult) {
        onSearchResult('smartSearch', {
          name: 'Busca Inteligente',
          color: 'blue',
          found: false,
          data: null,
          status: 'error',
          error: 'Erro na busca: ' + error.message
        });
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const detectedType = detectSearchType(searchTerm);
  const isLoading = loading.universal;

  return (
    <div className="card">
      <div className="p-6">
        <div className="flex items-center mb-4">
          <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 mr-3"></div>
          <h3 className="font-medium text-gray-900">Busca Inteligente</h3>
          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
            Nova Funcionalidade
          </span>
        </div>
        
        <div className="space-y-4">
          {/* Input de busca */}
          <div>
            <input
              type="text"
              className="input-field"
              placeholder="Digite PC, CNPJ ou CPF (ex: PC-16045, 12.345.678/0001-90, 123.456.789-00)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
            />
            
            {/* Indicador de tipo detectado */}
            {searchTerm && (
              <div className="mt-2 flex items-center text-xs text-gray-600">
                {detectedType === 'cpf' && (
                  <>
                    <FileText className="w-3 h-3 mr-1" />
                    <span>CPF detectado (11 dígitos)</span>
                  </>
                )}
                {detectedType === 'cnpj' && (
                  <>
                    <CreditCard className="w-3 h-3 mr-1" />
                    <span>CNPJ detectado (14 dígitos)</span>
                  </>
                )}
                {detectedType === 'pc_code' && (
                  <>
                    <Search className="w-3 h-3 mr-1" />
                    <span>Código PC detectado</span>
                  </>
                )}
                {detectedType === 'unknown' && (
                  <>
                    <Search className="w-3 h-3 mr-1" />
                    <span>Tipo não identificado</span>
                  </>
                )}
              </div>
            )}
          </div>

          
          {/* Botão de busca */}
          <button
            className="btn-primary w-full flex items-center justify-center space-x-2"
            onClick={handleSearch}
            disabled={isLoading || !searchTerm.trim()}
          >
            {isLoading ? (
              <div className="loading-spinner w-5 h-5" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            <span>
              {isLoading ? 'Buscando...' : 'Buscar Inteligente'}
            </span>
          </button>
          
          {/* Informações sobre a busca */}
          <div className="text-xs text-gray-500 space-y-1">
            <div className="flex items-center">
              <Database className="w-4 h-4 mr-1" />
              <span>Busca universal: detecta automaticamente PC, CPF ou CNPJ</span>
            </div>
          </div>

          {/* Status da última busca (apenas indicador simples) */}
          {lastResult && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center text-sm">
                {lastResult.success ? (
                  <>
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-green-700">
                      {lastResult.totalFound > 1 
                        ? `${lastResult.totalFound} cobranças encontradas! →` 
                        : 'Encontrado! Verifique o painel central →'}
                    </span>
                  </>
                ) : (
                  <>
                    <Clock className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-red-700">Não encontrado</span>
                  </>
                )}
              </div>
              {lastResult.success && (
                <div className="text-xs text-gray-600 mt-1">
                  Fonte: {lastResult.searchSource === 'local_database' ? '🗄️ Banco Local' : 
                         lastResult.searchSource === 'api_fallback' ? '🌐 API Externa' :
                         lastResult.searchSource === 'api_and_saved_to_local' ? '🌐➡️🗄️ API → Banco' : 
                         'Desconhecida'} ({lastResult.executionTime}ms)
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartSearchPanel;
